// frontend/src/pages/Home/HomePage.js

import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Navbar from '../../components/Navbar/Navbar';
import { ArrowRight, CheckCircle, Folder, Users, Shield, BarChart3, DollarSign } from 'lucide-react';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-slate-50/50 text-slate-900 font-sans selection:bg-emerald-100 selection:text-emerald-900 relative overflow-hidden">
      <Navbar />

      {/* Grid Overlay Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f060_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f060_1px,transparent_1px)] bg-[size:3.5rem_3.5rem] pointer-events-none"></div>

      {/* Top Ambient Glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-b from-emerald-200/40 via-emerald-100/20 to-transparent rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute top-20 left-0 w-[400px] h-[400px] bg-gradient-to-b from-blue-200/30 via-indigo-100/20 to-transparent rounded-full blur-3xl pointer-events-none"></div>

      {/* Hero Section */}
      <section className="pt-36 pb-20 relative z-10">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Hero Left Column: Presentation & Title */}
          <div className="lg:col-span-5 space-y-6 text-left">
            {/* Status Pill Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white border border-slate-200 shadow-sm rounded-full font-mono text-[11px] text-slate-700 tracking-tight">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>LIVE · SEGUIMIENTO DE PROYECTOS EN TIEMPO REAL</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-slate-950 tracking-tight leading-[1.1]">
              Gestiona lo que tu equipo <br className="hidden sm:inline" />
              realmente <span className="text-blue-600">entrega.</span>
            </h1>

            {/* Subtitle */}
            <p className="text-slate-600 text-base sm:text-lg max-w-xl leading-relaxed">
              CommitFlow consolida cada tarea, cada proyecto y cada liquidación de honorarios en una plataforma transparente que tu equipo puede auditar sin planillas ni procesos manuales.
            </p>

            {/* Action Buttons */}
            <div className="pt-2 flex flex-wrap items-center gap-3">
              <Link 
                to="/login" 
                className="px-6 py-3.5 bg-slate-950 hover:bg-slate-800 text-white text-sm font-semibold rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2 group"
              >
                <span>Comenzar ahora</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link 
                to="/quienes-somos" 
                className="px-6 py-3.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-800 text-sm font-semibold rounded-full shadow-sm hover:shadow transition-all"
              >
                Probar demo
              </Link>
            </div>

            {/* Monospaced Subcaption */}
            <div className="pt-1 font-mono text-[11px] text-slate-400 space-x-2">
              <span>sin tarjeta de crédito</span>
              <span>·</span>
              <span>inicio en 1 minuto</span>
              <span>·</span>
              <span>modo demo standalone</span>
            </div>
          </div>

          {/* Hero Right Column: Mockup Panel de Control Unificado */}
          <div className="lg:col-span-7 relative">
            <div className="bg-slate-950 rounded-3xl overflow-hidden shadow-2xl border border-slate-800 relative z-10">
              
              {/* Mockup Top Browser Bar */}
              <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-800 bg-slate-900/90">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-500/80"></div>
                </div>
                <div className="flex-1 mx-4">
                  <div className="max-w-xs mx-auto h-6 bg-slate-800 rounded-md flex items-center justify-center px-3 border border-slate-700/60 font-mono text-[11px] text-slate-400">
                    commitflow.app/dashboard
                  </div>
                </div>
              </div>

              {/* Mockup Dashboard Content */}
              <div className="p-5 sm:p-6 bg-slate-950">
                <div className="grid grid-cols-12 gap-4">
                  
                  {/* Mock Sidebar */}
                  <div className="col-span-3 hidden sm:block space-y-2.5">
                    <div className="h-9 bg-slate-900 rounded-xl w-full flex items-center px-3 border border-slate-800 font-mono text-xs text-slate-300">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 mr-2"></span>
                      CommitFlow
                    </div>
                    <div className="h-7 bg-slate-900/50 rounded-xl w-full border border-slate-800/40"></div>
                    <div className="h-7 bg-slate-900/50 rounded-xl w-full border border-slate-800/40"></div>
                    <div className="h-7 bg-slate-900/50 rounded-xl w-full border border-slate-800/40"></div>
                  </div>

                  {/* Mock Main Area */}
                  <div className="col-span-12 sm:col-span-9 space-y-3.5 font-mono">
                    <div className="grid grid-cols-3 gap-2.5">
                      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3">
                        <div className="text-xl sm:text-2xl font-bold text-white">24</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Tareas Totales</div>
                      </div>
                      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3">
                        <div className="text-xl sm:text-2xl font-bold text-emerald-400">18</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Completadas</div>
                      </div>
                      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3">
                        <div className="text-xl sm:text-2xl font-bold text-amber-400">6</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">En Progreso</div>
                      </div>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3.5 space-y-2">
                      <div className="flex justify-between text-xs font-semibold">
                        <span className="text-slate-300">Progreso del Sprint Actual</span>
                        <span className="text-emerald-400">75%</span>
                      </div>
                      <div className="h-2 bg-slate-950 rounded-full overflow-hidden">
                        <div className="h-full w-3/4 bg-gradient-to-r from-blue-500 to-emerald-400 rounded-full"></div>
                      </div>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-2xl divide-y divide-slate-800/60 overflow-hidden text-xs">
                      {[
                        { title: 'Diseñar landing page', done: true },
                        { title: 'Revisar documentación técnica', done: false },
                        { title: 'Despliegue a producción', done: false }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3">
                          <div className={`w-4 h-4 rounded-full border ${item.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600'} flex items-center justify-center`}>
                            {item.done && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <span className={item.done ? 'text-slate-500 line-through' : 'text-slate-200'}>{item.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>

            </div>

            {/* Decorative Glow Effect Behind Mockup */}
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 rounded-3xl blur-2xl opacity-50 -z-10"></div>
          </div>

        </div>
      </section>

      {/* Separate Rounded Floating Pill Badges */}
      <section className="py-8 relative z-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-center gap-3">
          {[
            { text: "AUTOPILOTO", dotColor: "bg-blue-500" },
            { text: "REPORTES EN TIEMPO REAL", dotColor: "bg-emerald-500" },
            { text: "SIN PLANILLAS", dotColor: "bg-amber-500" },
            { text: "RETENCIÓN 14.5% AUTOMÁTICA", dotColor: "bg-indigo-500" },
            { text: "CERO ERRORES", dotColor: "bg-teal-500" }
          ].map((pill, idx) => (
            <div 
              key={idx}
              className="bg-white/90 backdrop-blur-md border border-slate-200/90 shadow-sm rounded-full px-4 py-2 font-mono text-[11px] font-semibold text-slate-700 hover:border-slate-300 hover:shadow transition-all flex items-center gap-2"
            >
              <span className={`w-2 h-2 rounded-full ${pill.dotColor}`}></span>
              <span>{pill.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white border-t border-slate-200/80 relative z-10">
        <div className="max-w-5xl mx-auto px-6">
          
          <div className="text-center mb-14">
            <span className="font-mono text-xs font-bold text-blue-600 uppercase tracking-widest">FUNCIONALIDADES</span>
            <h2 className="text-3xl font-extrabold text-slate-950 mt-1 tracking-tight">Diseñado para Equipos Modernos</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                tag: "PROYECTOS",
                icon: <Folder className="w-4 h-4" />,
                title: "Gestión de Proyectos",
                desc: "Organización de etapas, plazos y requerimientos en tableros consolidados."
              },
              {
                tag: "TAREAS",
                icon: <CheckCircle className="w-4 h-4" />,
                title: "Control de Entregables",
                desc: "Asignación individual, seguimiento de avances y recepción de evidencias."
              },
              {
                tag: "COLABORACIÓN",
                icon: <Users className="w-4 h-4" />,
                title: "Invitación de Equipos",
                desc: "Integración fluida entre administradores, jefes de proyecto y colaboradores."
              },
              {
                tag: "REPORTE",
                icon: <BarChart3 className="w-4 h-4" />,
                title: "Informes & Gráficas",
                desc: "Métricas visuales de avance real vs planificado y solapamiento de fechas."
              },
              {
                tag: "FINANZAS",
                icon: <DollarSign className="w-4 h-4" />,
                title: "Liquidaciones & Retención",
                desc: "Cálculo de horas, monto bruto, retención del 14.5% y monto líquido disponible en PDF."
              },
              {
                tag: "SEGURIDAD",
                icon: <Shield className="w-4 h-4" />,
                title: "Auditoría de Código",
                desc: "Healthcheck sintáctico integrado que previene inconsistencias de datos."
              }
            ].map((f, idx) => (
              <div 
                key={idx}
                className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-6 hover:border-slate-300 hover:shadow-md transition-all space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="w-9 h-9 rounded-xl bg-slate-950 text-white flex items-center justify-center">
                    {f.icon}
                  </div>
                  <span className="font-mono text-[10px] font-bold text-slate-400 tracking-wider">{f.tag}</span>
                </div>
                <h3 className="text-base font-bold text-slate-950">{f.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CTA Bottom Banner */}
      <section className="py-20 bg-slate-950 text-white relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6 relative z-10">
          <span className="font-mono text-xs font-bold text-emerald-400 uppercase tracking-widest">INICIO INMEDIATO</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Prueba la Plataforma en Modo Demo
          </h2>
          <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
            Accede al instante como Administrador, Jefe de Proyecto o Colaborador sin necesidad de registro previo.
          </p>
          <div className="pt-2">
            <Link 
              to="/login"
              className="px-8 py-3.5 bg-white text-slate-950 hover:bg-slate-100 text-sm font-semibold rounded-full shadow-xl transition-all inline-flex items-center gap-2"
            >
              <span>Acceder al Panel Demo</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}